<template>
    <div class="payment-details">
        <h1 class="payment-details--title">{{$t('REGISTER_PAYMENT_DETAILS_TITLE')}}</h1>
        <p class="payment-details--text">{{$t('REGISTER_PAYMENT_DETAILS_TEXT')}}</p>

        <div class="payment-details--total">
            <total-section/>
        </div>

        <div class="payment-details--card-wallet">
            <loader v-if="isfetchingSetupIntent" />
            <div class="payment-details--radio-container" v-if="!isfetchingSetupIntent">
                <radio-field-new v-for="payment in payments" v-show="payment.key !== 'wallet' || (payment.key === 'wallet' && loadWallet)" :key="payment.key" :item="payment" v-model="selectedRadio" :selected="selectedRadio" @clicked="toggleSelected">
                    <component :is="payment.component" @register="registerCustomer" v-bind="additionalProperties(payment)"/>
                </radio-field-new>
            </div>
        </div>

        <checkbox
            :initialValue = "deliveryAddressIsBilling"
            :smallFont="true"
            v-model="deliveryAddressIsBilling">
            {{ $t('BILLING_ADDRESS_FORM_BILLING_ADDRESS') }}
        </checkbox>

        <div>
            <div :class="billingAddressContainerClass" :style="billingAddressContainerStyle">
                <billing-address-form
                    :error-handler="errorHandler"
                    v-model="billingAddress"/>
            </div>
        </div>

        <span class="payment-details--error" v-if="errorHandler.response.error" v-text="errorHandler.response.error"></span>

        <div class="payment-details--next-button">
            <submit-button
                :processing="processing"
                color="green"
                @click="submitCurrentStep()">
                {{ $t('CARD_DETAILS_FORM_SIGN_ME_UP') }}
            </submit-button>
        </div>

        <supported-cards :payments="payments" height="30px"/>
        <div class="payment-details--info">
            <p class="payment-details--info--text">{{$t('REGISTER_PAYMENT_CHARGED')}}</p>
            <p class="payment-details--info--text bold">{{$t('REGISTER_PAYMENT_CANCLE_ANYTIME')}}</p>
        </div>

        <alt-trust-pilot class="payment-details--trust-pilot"/>
        <simple-footer />
    </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import { SECTION_PAYMENT } from '@/constants/Registration';
import { USER_TYPE } from '@/constants/Account';
import ErrorResponseHandler from '@/helpers/ErrorResponseHandler';
import AuthService from '@/services/auth/AuthService';
import RegistrationService from '@/services/users/RegistrationService';
import Checkbox from '@/components/forms/fields/Checkbox';
import SubmitButton from '@/components/forms/buttons/SubmitButton';
import BillingAddressForm from '@/components/forms/addresses/BillingAddressForm';
import SupportedCards from '@/components/forms/fields/SupportedCards';
import StripeElementsForm from '@/components/forms/cardDetails/StripeElementsForm';
import TotalSection from '@/components/forms/fields/TotalSection';
import SimpleFooter from '@/components/structure/SimpleFooter';
import AltTrustPilot from '@/components/forms/fields/AltTrustPilot';
import { CSPageEvents } from '@/helpers/GAHelpers';
import Loader from '@/components/structure/Loader';
import RadioFieldNew from '../fields/RadioFieldNew';
import CountryService from '@/services/logistics/CountryService';

const paymentsForm = require('@/helpers/PaymentHelpers').payments.filter(item => item.enabled);

export default {
    name: 'PaymentScreen',
    components: {
        Checkbox,
        SubmitButton,
        BillingAddressForm,
        StripeElementsForm,
        SupportedCards,
        TotalSection,
        AltTrustPilot,
        SimpleFooter,
        Loader,
        RadioFieldNew,
    },
    data() {
        return {
            processing: false,
            showCouponCode: false,
            userHasRequesed: false,
            billingAddressOverflowVisible: false,
            errorHandler: new ErrorResponseHandler(),
            error: null,
            price: process.env.VUE_APP_BOX_PRICE,
            payments: [],
            stripe: null,
            elements: null,
            prButton: null,
            paymentRequest: null,
            loadWallet: false,
            selectedRadio: paymentsForm && paymentsForm.filter(item => item.isSelected === true)[0]?.key,
        };
    },
    beforeCreate() {
        this.$store.dispatch('cookies/mountStripe');
    },
    created() {
        this.canMakePayment();
    },
    mounted() {
        this.fetchStripeSetupIntent({ email: this.email, gateway: this.paymentGateway, payment_method: 'card' });
    },
    computed: {
        ...mapGetters({
            currencyCode: 'localisation/getCurrencyCode',
            email: 'register/getEmail',
            fullName: 'register/getFullName',
            firstDeliveryDate: 'register/getFirstDeliveryDay',
            stripeMounted: 'cookies/isStripeMounted',
            selectedPlan: 'register/getSelectedPlan',
            selectedProductDetails: 'register/getSelectedProductDetails',
            setupIntent: 'account/getSetupIntent',
            isfetchingSetupIntent: 'account/isfetchingSetupIntent',
            paymentGateway: 'payment/getStripeGateway',
        }),

        billingAddress: {
            get() {
                return this.$store.getters['register/getBillingAddress'];
            },
            set(value) {
                this.$store.commit('register/setBillingAddress', value);
            },
        },

        deliveryAddress: {
            get() {
                return this.$store.getters['register/getDeliveryAddress'];
            },
            set(value) {
                this.$store.commit('register/setDeliveryAddress', value);
            },
        },

        friendCode: {
            get() {
                return this.$store.getters['register/getFriendCode'];
            },
            set(code) {
                this.$store.commit('register/setFriendCode', code);
            },
        },

        deliveryAddressIsBilling: {
            get() {
                return this.$store.getters['register/getDeliveryAddressIsBilling'];
            },
            set(value) {
                this.$store.commit('register/setDeliveryAddressIsBilling', value);
            },
        },

        billingAddressContainerClass() {
            return !this.deliveryAddressIsBilling ? 'accordion__md accordion__md--active' : 'accordion__md';
        },

        billingAddressContainerStyle() {
            return this.billingAddressOverflowVisible ? { overflow: 'visible' } : {};
        },
        showProductTypeSelect() {
            return this.$globalQueryParams?.bumper === 'true';
        },
    },
    methods: {
        ...mapActions({
            fetchStripeSetupIntent: 'account/fetchStripeSetupIntent',
        }),

        canMakePayment() {
            setTimeout(() => {
                this.stripe = Stripe(process.env.VUE_APP_STRIPE_PUBLISHABLE_KEY);
                let country = CountryService.findCountryByIso(process.env.VUE_APP_COUNTRY_ISO);
                this.paymentRequest = this.stripe.paymentRequest({
                    country: country.iso2,
                    currency: this.currencyCode.toLowerCase(),
                    requestPayerName: true,
                    requestPayerEmail: true,
                    total: {
                        label: 'Order Total',
                        amount: Number(process.env.VUE_APP_BOX_PRICE) * 100 || 0,
                    },
                });

                this.elements = this.stripe.elements();
                this.prButton = this.elements.create('paymentRequestButton', {
                    paymentRequest: this.paymentRequest,
                });

                try {
                    this.paymentRequest.canMakePayment()
                        .then((result) => {
                            if (result) {
                                this.loadWallet = true;
                                this.prButton.mount('#payment-request-button');
                            } else {
                                this.loadWallet = false;
                            }
                        });
                } catch (err) {
                    console.log(err);
                } finally {
                    this.payments = paymentsForm;
                }
            }, 1000);
        },

        // Handle validating and submitting this step.
        submitCurrentStep() {
            this.userHasRequesed = true;
            this.error = null;
            this.processing = true;

            this.$refs.cardForm.handleScaCardSubmission()
                .then(tokenData => {
                    this.registerCustomer(tokenData.setupIntentId, tokenData.setupIntentId);
                })
                .catch((err) => {
                    console.log(err);
                    this.error = err;
                    this.handleFail();
                });
        },

        // Continue to register the customer with our API.
        registerCustomer(tokenId, cardId, paymentMethod = 'card', nameOnCard) {
            RegistrationService.registerCustomer(tokenId, cardId, paymentMethod, nameOnCard)
                .then(response => {
                    CSPageEvents(undefined, {
                        frequency: this.selectedPlan?.plan_interval,
                        productName: this.selectedProductDetails?.plan_name.toLowerCase() + 'Box',
                        transactionId: response?.data?.subscription_id,
                        revenue: Number(response?.data?.price),
                        shippingAmount: 0, // Since delivery cost on a new signup is free!
                        currency: this.currencyCode,
                        productType: 'flowerSubscription',
                    });
                    this.$store.commit('register/setSectionComplete', SECTION_PAYMENT);
                    this.handleSuccessfulRegistration();
                })
                .catch(error => {
                    this.handleFail();
                    this.$store.commit('account/setSetupIntent', null);
                    const hasAmex = error.response?.data?.error.includes('Amex');
                    if (hasAmex) return this.errorHandler.setResponse({ error: this.$t('AMERICAN_EXPRESS_NOT_SUPPORTED') });
                    setTimeout(() => this.errorHandler.setResponse(error.response?.data), 0);
                });
        },
        // Handle failed sign up or card token request.
        handleFail() {
            this.processing = false;
            this.$scrollTo(`.input--invalid`, {
                offset: -100,
            });
            this.fetchStripeSetupIntent({ email: this.email, gateway: this.paymentGateway, payment_method: 'card' });
        },

        additionalProperties(component) {
            if (component.key === 'wallet') {
                return {
                    setupIntent: this.setupIntent,
                };
            };
            if (component.key === 'card') {
                return {
                    ref: 'cardForm',
                    fullName: this.fullName,
                    billingAddress: this.deliveryAddressIsBilling ? this.deliveryAddress : this.billingAddress,
                    hasBeenRequested: this.userHasRequesed,
                };
            };
        },

        toggleSelected(id) {
            this.selectedRadio = id;
            return this.payments.map(item => {
                if (item.key === id) {
                    item.isSelected = true;
                } else {
                    item.isSelected = false;
                }
            });
        },

        async handleSuccessfulRegistration() {
            let email = this.$store.getters['register/getEmail'];
            let password = this.$store.getters['register/getPassword'];
            let authService = new AuthService();

            const response = await authService.login(email, password);
            localStorage.setItem('access_token', response.data.tokens.access_token);
            localStorage.setItem('refresh_token', response.data.tokens.refresh_token);

            this.$store.dispatch('auth/loginUser', USER_TYPE);

            this.$router.push({ name: 'account.account-confirmation' });
        },
    },
};
</script>

<style lang="scss" scoped>
.payment-details {
    margin: 10px auto;

    &--title {
        font-size: 25px;
        line-height: 130%;
        color: #262323;
        margin: 0;
    }
    &--text {
        font-size: 16px;
        line-height: 140%;
        color: #262323;
        margin: 16px 0;
        &--bold {
            font-weight: 600;
            font-size: 16px;
            line-height: 140%;
            color: #262323;
            margin: 20px 0;
        }
    }
    &--next-button {
        width: 80%;
        margin: 20px auto;
        display: flex;
        align-items: center;
        justify-content: center;
    }
    &--info {
        text-align: center;
        width: 90%;
        margin: 20px auto;
            &--text {
            font-size: 16px;
            line-height: 140%;
            color: #262323;
            margin: 0;
            &.bold {
                font-weight: 600;
            }
        }
    }
    &--total {
        margin: 20px auto;
        padding: 20px 10px;
        background: #F7F7F7;
        @media screen and (min-width: 640px) {
            display: none;
        }
    }
    // &--card-wallet {
    //     border-radius: 4px;
    // }
    &--radio-container {
        width: 100%;
        border: 1px solid #DFDFDF;
        border-radius: 4px;
    }
    &--trust-pilot {
        margin-top: 50px;
        width: 100%;
    }
    &--error {
        font-weight: 300;
        font-size: 16px;
        color: #b1473c;
    }
}
</style>
